import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { WORKER_API_URL, getEnvVar } from './config';

// Debug flag for verbose axios logs in this module
const SUBS_DEBUG = (
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_DEBUG_SUBSCRIPTIONS === 'true') ||
  getEnvVar('VITE_DEBUG_SUBSCRIPTIONS') === 'true'
);

// Add axios interceptors only when debug flag is enabled
if (SUBS_DEBUG) {
  let requestCounter = 0;
  axios.interceptors.request.use(
    (config) => {
      const requestId = `REQ-${++requestCounter}-${Date.now()}`;
      (config.headers as any)['X-Request-ID'] = requestId;
      console.log(`🚀 [${requestId}] Axios Request:`, {
        url: config.url,
        method: config.method,
        headers: config.headers,
        data: config.data,
        baseURL: config.baseURL,
        timestamp: new Date().toISOString()
      });
      return config;
    },
    (error) => {
      console.error('❌ Axios Request Setup Error:', error);
      return Promise.reject(error);
    }
  );

  axios.interceptors.response.use(
    (response) => {
      const requestId = (response.config.headers as any)['X-Request-ID'] || 'UNKNOWN';
      console.log(`✅ [${requestId}] Axios Response:`, {
        url: response.config.url,
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers,
      });
      return response;
    },
    (error) => {
      const requestId = (error.config?.headers as any)?.['X-Request-ID'] || 'UNKNOWN';
      console.error(`❌ [${requestId}] Axios Response Error:`, {
        url: error.config?.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });
      return Promise.reject(error);
    }
  );
}

// Initialize Stripe.js with publishable key
// Use direct static access for Vite to ensure proper build-time replacement
const STRIPE_KEY =
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
  getEnvVar('VITE_STRIPE_PUBLISHABLE_KEY') ||
  getEnvVar('REACT_APP_STRIPE_PUBLISHABLE_KEY') ||
  '';
console.log('Stripe key configured:', STRIPE_KEY ? `${STRIPE_KEY.substring(0, 20)}...` : 'NOT SET');
if (!STRIPE_KEY) {
  // In local/dev (e.g., /chat-test), avoid initializing Stripe when key is absent
  console.warn('Stripe publishable key is not configured. Skipping Stripe initialization for this session.');
}
// Lazily initialize; when key is empty, provide a null-resolving promise to avoid IntegrationError
const stripePromise: Promise<any> = STRIPE_KEY ? loadStripe(STRIPE_KEY) : Promise.resolve(null);

export interface SubscriptionPlan {
  id: string;
  name: string;
  tier: 'free' | 'plus' | 'pro';
  price: number;
  interval: 'month' | 'year';
  stripe_price_id: string;
  features: Record<string, any>;
  is_active: boolean;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id?: string;
  plan?: SubscriptionPlan;
  status: 'inactive' | 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';
  current_period_start?: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  is_active: boolean;
  is_canceled: boolean;
  tier: string;
  // Optional Stripe-related identifiers and metadata that may be present
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  // Some responses may echo price id at subscription-level; treat as optional
  stripe_price_id?: string;
  created_at?: string;
  updated_at?: string;
  trial_end?: string;
}

export interface SubscriptionStatus {
  has_subscription: boolean;
  subscription?: UserSubscription;
  usage_limits?: {
    tier: string;
    limits: Record<string, number | string>;
    current_usage: Record<string, number>;
    period_start: string;
    period_end: string;
  };
  // Upcoming or scheduled plan change information (if any)
  scheduled_change?: {
    switch_type: 'upgrade' | 'downgrade';
    target_price_id: string;
    target_tier: string;
    effective_date: string;
  };
}

export interface PaymentHistory {
  id: string;
  amount: number;
  currency: string;
  status: string;
  description?: string;
  created_at: string;
}

export interface CheckoutSession {
  checkout_url: string;
  session_id: string;
}

export interface CustomerPortal {
  portal_url: string;
}

export interface PlanSwitchPreview {
  current_price_id: string;
  new_price_id: string;
  proration_amount: number;
  immediate_charge: number;
  net_change: number;
  current_period_end: string;
  billing_cycle_anchor: number;
  preview_total: number;
  currency: string;
  is_upgrade: boolean;
  has_default_payment_method?: boolean;
}

export interface PlanSwitchRequest {
  new_price_id: string;
  proration_behavior?: 'create_prorations' | 'none' | 'always_invoice';
  // When true, backend should release an active/not_started subscription schedule
  // before applying the plan change. This is used to "override" scheduled updates.
  override_schedule?: boolean;
}

export interface PlanSwitchResponse {
  success: boolean;
  subscription: UserSubscription;
  message: string;
  switch_type: 'upgrade' | 'downgrade';
  effective_date: string;
}

export interface PlanSwitchHistory {
  id: string;
  amount: number;
  currency: string;
  status: string;
  description?: string;
  created_at: string;
  metadata: Record<string, any>;
}

class SubscriptionService {
  private buildLocalFallbackPlans(): SubscriptionPlan[] {
    const freeId = process.env.REACT_APP_STRIPE_PRICE_ID_FREE || '';
    const plusMonthlyId = process.env.REACT_APP_STRIPE_PRICE_ID_PLUS_MONTHLY || '';
    const proMonthlyId = process.env.REACT_APP_STRIPE_PRICE_ID_PRO_MONTHLY || '';

    // const nowIso = new Date().toISOString();
    const makePlan = (
      id: string,
      name: string,
      tier: 'free' | 'plus' | 'pro',
      interval: 'month' | 'year',
      price: number,
      stripe_price_id: string
    ): SubscriptionPlan => ({
      id,
      name,
      tier,
      interval,
      price,
      stripe_price_id,
      // Minimal features listing; the UI supplies its own display list
      features: { base: true },
      is_active: true,
    });

    return [
      makePlan('local-free', 'Free Monthly', 'free', 'month', 0, freeId),
      makePlan('local-plus', 'Plus Monthly', 'plus', 'month', 30, plusMonthlyId),
      makePlan('local-pro', 'Pro Monthly', 'pro', 'month', 150, proMonthlyId),
    ];
  }

  private getAuthHeader() {
    const token = localStorage.getItem('token');
    if (SUBS_DEBUG) {
      console.log('📝 getAuthHeader from localStorage:', {
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'NO TOKEN'
      });
    }
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // New method that accepts auth headers from context
  private getHeaders(authHeaders?: Record<string, string>) {
    const headers = {
      ...(authHeaders || this.getAuthHeader()),
      'Content-Type': 'application/json',
    } as Record<string, string>;
    if (SUBS_DEBUG) {
      console.log('📋 getHeaders result:', {
        hasAuthHeaders: !!authHeaders,
        usingFallback: !authHeaders,
        hasAuthorization: !!headers.Authorization,
        headerKeys: Object.keys(headers)
      });
    }
    return headers;
  }

  async getPlans(signal?: AbortSignal): Promise<SubscriptionPlan[]> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    // Use provided signal or internal controller's signal
    const effectiveSignal = signal || controller.signal;
    
    try {
      const response = await axios.get<SubscriptionPlan[]>(
        `${WORKER_API_URL}/subscriptions/plans`,
        { 
          headers: this.getHeaders(),
          signal: effectiveSignal as any 
        }
      );
      const plans = Array.isArray(response.data) ? response.data : [];
      if (plans.length === 0) {
        console.warn('Plans API returned empty list; using local fallback plans.');
        return this.buildLocalFallbackPlans();
      }
      return plans;
    } catch (error: any) {
      // Don't fall back to local plans if the request was explicitly aborted
      if (error.code === 'ERR_CANCELED' && signal?.aborted) {
        console.log('Plans API request was intentionally aborted');
        throw error;
      }
      console.error('Error fetching subscription plans, using local fallback plans:', error);
      return this.buildLocalFallbackPlans();
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async getCurrentSubscription(authHeaders?: Record<string, string>): Promise<SubscriptionStatus> {
    const headers = this.getHeaders(authHeaders);
    const url = `${WORKER_API_URL}/subscriptions/current`;
    try {
      const response = await axios.get<SubscriptionStatus>(url, { headers });
      return response.data;
    } catch (err: any) {
      // If backend is temporarily failing, synthesize a safe fallback instead of bubbling the error
      const status = err?.response?.status as number | undefined;
      const body = err?.response?.data as any;
      const isServerError = typeof status === 'number' && status >= 500;
      const isKnownMessage = typeof body === 'object' && body?.error && /fetch subscription/i.test(String(body.error));

      if (SUBS_DEBUG) {
        console.error('Error fetching current subscription:', {
          status,
          body,
          url,
        });
      }

      if (isServerError || isKnownMessage) {
        // Default to free tier limits to keep UI functional
        const fallback: SubscriptionStatus = {
          has_subscription: false,
          usage_limits: {
            tier: 'free',
            limits: {
              ai_chat_sessions: 5,
              assessments: 'unlimited',
              custom_learning_paths: 0,
            },
            current_usage: {},
            period_start: new Date().toISOString(),
            period_end: new Date().toISOString(),
          },
        };
        if (!SUBS_DEBUG) {
          // Keep console clean in non-debug mode
        } else {
          console.warn('[subscriptions/current] Using fallback subscription status due to server error');
        }
        return fallback;
      }

      // For other cases (e.g., 401), rethrow so upstream handlers can act
      throw err;
    }
  }

  async createCheckoutSession(
    priceId: string,
    successUrl: string,
    cancelUrl: string,
    authHeaders?: Record<string, string>,
    enableTrial?: boolean
  ): Promise<CheckoutSession> {
    const debugId = `CHECKOUT-${Date.now()}`;
    console.log(`🎯 [${debugId}] ========== STARTING CHECKOUT SESSION CREATION ==========`);
    
    try {
      const headers = this.getHeaders(authHeaders);
      
      console.log(`📋 [${debugId}] Headers being used:`, {
        hasAuthHeaders: !!authHeaders,
        hasAuthorization: !!headers.Authorization,
        authorizationPreview: headers.Authorization ? headers.Authorization.substring(0, 50) + '...' : 'MISSING',
        allHeaderKeys: Object.keys(headers)
      });
      
      // Validate that we have auth headers
      if (!headers.Authorization) {
        console.error(`❌ [${debugId}] FATAL: No authorization header available for checkout session`);
        console.error(`📍 [${debugId}] Debug info:`, {
          authHeadersProvided: !!authHeaders,
          localStorageToken: localStorage.getItem('token') ? 'Present' : 'Missing',
          localStorageTokenPreview: localStorage.getItem('token')?.substring(0, 30) + '...'
        });
        throw new Error('Authentication required. Please log in and try again.');
      }
      
      // Decode and validate token before sending
      try {
        const token = headers.Authorization.replace('Bearer ', '');
        const tokenParts = token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log(`🔐 [${debugId}] Token validation:`, {
            userId: payload.sub,
            issuedAt: new Date(payload.iat * 1000).toISOString(),
            expiresAt: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'No expiry',
            isExpired: payload.exp ? Date.now() > payload.exp * 1000 : false,
            secondsUntilExpiry: payload.exp ? Math.floor((payload.exp * 1000 - Date.now()) / 1000) : 'N/A'
          });
          
          if (payload.exp && Date.now() > payload.exp * 1000) {
            console.error(`⏰ [${debugId}] TOKEN IS EXPIRED!`);
          }
        }
      } catch (e) {
        console.warn(`⚠️ [${debugId}] Could not validate token format:`, e.message);
      }
      
      const fullUrl = `${WORKER_API_URL}/subscriptions/create-checkout`;
      const requestBody: any = {
        price_id: priceId,
        success_url: successUrl,
        cancel_url: cancelUrl
      };
      if (enableTrial) requestBody.enable_trial = true;
      
      console.log(`📤 [${debugId}] Sending checkout request:`, {
        url: fullUrl,
        workerApiUrl: WORKER_API_URL,
        method: 'POST',
        body: requestBody,
        headersPreview: {
          Authorization: headers.Authorization?.substring(0, 50) + '...',
          'Content-Type': headers['Content-Type'] || 'application/json'
        }
      });
      
      console.log(`⏱️ [${debugId}] Making axios POST request now...`);
      const startTime = Date.now();
      
      // Add cache-busting headers to force fresh request
      const requestHeaders = {
        ...headers,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        'X-Debug-Timestamp': Date.now().toString()
      };
      
      const response = await axios.post<CheckoutSession>(
        fullUrl,
        requestBody,
        { headers: requestHeaders }
      );
      
      const duration = Date.now() - startTime;
      console.log(`✅ [${debugId}] Checkout session created successfully in ${duration}ms:`, {
        checkoutUrl: response.data.checkout_url,
        sessionId: response.data.session_id,
        responseStatus: response.status
      });
      
      return response.data;
    } catch (error) {
      console.error(`💥 [${debugId}] ========== CHECKOUT SESSION CREATION FAILED ==========`);
      console.error(`💥 [${debugId}] Error details:`, {
        message: error.message,
        isAxiosError: axios.isAxiosError(error),
        status: axios.isAxiosError(error) ? error.response?.status : 'N/A',
        statusText: axios.isAxiosError(error) ? error.response?.statusText : 'N/A',
        responseData: axios.isAxiosError(error) ? error.response?.data : 'N/A'
      });
      
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) {
          console.error(`🔍 [${debugId}] 404 ERROR - Possible causes:`, {
            attemptedUrl: `${WORKER_API_URL}/subscriptions/create-checkout`,
            possibleIssues: [
              '1. Worker API routing issue - endpoint not registered',
              '2. Auth middleware returning 404 instead of proper error',
              '3. CORS preflight failing and misreported as 404',
              '4. Worker API deployment not updated'
            ]
          });
        } else if (error.response?.status === 401) {
          console.error(`🔒 [${debugId}] 401 UNAUTHORIZED - Authentication failed:`, {
            message: 'Token was rejected by the Worker API',
            suggestions: [
              '1. Token is expired',
              '2. Token is invalid',
              '3. Worker API cannot validate token',
              '4. Backend auth service is down'
            ]
          });
        }
      }
      
      throw error;
    }
  }

  async cancelSubscription(cancelImmediately: boolean = false, authHeaders?: Record<string, string>): Promise<UserSubscription> {
    try {
      const response = await axios.post<UserSubscription>(
        `${WORKER_API_URL}/subscriptions/cancel`,
        { cancel_immediately: cancelImmediately },
        { headers: this.getHeaders(authHeaders) }
      );
      return response.data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  async reactivateSubscription(authHeaders?: Record<string, string>): Promise<UserSubscription> {
    try {
      const response = await axios.post<UserSubscription>(
        `${WORKER_API_URL}/subscriptions/reactivate`,
        {},
        { headers: this.getHeaders(authHeaders) }
      );
      return response.data;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  }

  async createCustomerPortal(returnUrl: string, authHeaders?: Record<string, string>): Promise<CustomerPortal> {
    try {
      const response = await axios.post<CustomerPortal>(
        `${WORKER_API_URL}/subscriptions/portal`,
        { return_url: returnUrl },
        { headers: this.getHeaders(authHeaders) }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating customer portal:', error);
      throw error;
    }
  }

  async getPaymentHistory(skip: number = 0, limit: number = 100, authHeaders?: Record<string, string>): Promise<PaymentHistory[]> {
    try {
      const response = await axios.get<PaymentHistory[]>(
        `${WORKER_API_URL}/subscriptions/payment-history`,
        {
          params: { skip, limit },
          headers: this.getHeaders(authHeaders)
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }

  async redirectToCheckout(priceId: string, options?: { enableTrial?: boolean }): Promise<void> {
    const successUrl = `${window.location.origin}/chat?subscription=success`;
    const cancelUrl = `${window.location.origin}/pricing`;

    try {
      const session = await this.createCheckoutSession(priceId, successUrl, cancelUrl, undefined, options?.enableTrial);
      
      if (session.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = session.checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  }

  async redirectToCustomerPortal(): Promise<void> {
    const returnUrl = `${window.location.origin}/chat`;

    try {
      const portal = await this.createCustomerPortal(returnUrl);
      
      if (portal.portal_url) {
        // Redirect to Stripe Customer Portal
        window.location.href = portal.portal_url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Error redirecting to customer portal:', error);
      throw error;
    }
  }

  // Helper method to get Stripe instance
  async getStripe() {
    return stripePromise;
  }

  async previewPlanChange(
    newPriceId: string,
    authHeaders?: Record<string, string>
  ): Promise<PlanSwitchPreview> {
    const debugId = `PREVIEW-CHANGE-${Date.now()}`;
    console.log(`🔍 [${debugId}] ========== PREVIEW PLAN CHANGE ==========`);
    console.log(`📋 [${debugId}] Preview request:`, {
      newPriceId,
      hasAuthHeaders: !!authHeaders,
      timestamp: new Date().toISOString()
    });

    try {
      const headers = this.getHeaders(authHeaders);
      
      console.log(`📤 [${debugId}] Making preview request...`);
      const startTime = Date.now();
      
      const response = await axios.post<PlanSwitchPreview>(
        `${WORKER_API_URL}/subscriptions/preview-plan-change`,
        { new_price_id: newPriceId },
        { headers }
      );
      
      const duration = Date.now() - startTime;
      console.log(`✅ [${debugId}] Preview generated successfully in ${duration}ms:`, {
        isUpgrade: response.data.is_upgrade,
        netChange: response.data.net_change,
        currency: response.data.currency
      });
      
      return response.data;
    } catch (error) {
      console.error(`💥 [${debugId}] Preview plan change failed:`, error);
      throw error;
    }
  }

  async changePlan(
    request: PlanSwitchRequest,
    authHeaders?: Record<string, string>
  ): Promise<PlanSwitchResponse> {
    const debugId = `CHANGE-PLAN-${Date.now()}`;
    console.log(`🔄 [${debugId}] ========== CHANGE PLAN REQUEST ==========`);
    console.log(`📋 [${debugId}] Plan change request:`, {
      newPriceId: request.new_price_id,
      prorationBehavior: request.proration_behavior || 'create_prorations',
      hasAuthHeaders: !!authHeaders,
      timestamp: new Date().toISOString()
    });

    try {
      const headers = this.getHeaders(authHeaders);
      
      console.log(`📤 [${debugId}] Making plan change request...`);
      const startTime = Date.now();
      const payload = {
        new_price_id: request.new_price_id,
        proration_behavior: request.proration_behavior || 'create_prorations',
        // Include multiple override flags for backend compatibility
        override_schedule: !!request.override_schedule,
        override: !!request.override_schedule,
        release_schedule: !!request.override_schedule,
        force: !!request.override_schedule,
      } as Record<string, any>;

      console.log(`🧪 [${debugId}] Request payload preview:`, {
        keys: Object.keys(payload),
        override_schedule: payload.override_schedule,
        override: payload.override,
        release_schedule: payload.release_schedule,
        force: payload.force,
      });

      const response = await axios.post<PlanSwitchResponse>(
        `${WORKER_API_URL}/subscriptions/change-plan`,
        payload,
        { headers }
      );
      
      const duration = Date.now() - startTime;
      console.log(`✅ [${debugId}] Plan changed successfully in ${duration}ms:`, {
        switchType: response.data.switch_type,
        effectiveDate: response.data.effective_date,
        success: response.data.success
      });
      
      return response.data;
    } catch (error) {
      console.error(`💥 [${debugId}] Plan change failed:`, error);
      // Normalize schedule conflicts to a dedicated flag for consumers
      try {
        const axiosMod = (await import('axios')).default;
        if (axiosMod.isAxiosError(error)) {
          const detail: string | undefined = (error.response?.data as any)?.detail || (error.response?.data as any)?.message;
          if (detail && /schedule/i.test(detail)) {
            (error as any).reason = 'SCHEDULE_CONFLICT';
            (error as any).isScheduleConflict = true;
          }
          // Emit payload to help debugging
          console.log(`🧪 [${debugId}] Plan change failed; last payload:`, {
            override_schedule: !!request.override_schedule,
            proration_behavior: request.proration_behavior || 'create_prorations',
            new_price_id: request.new_price_id,
          });
        }
      } catch {}
      throw error;
    }
  }

  async getPlanSwitchHistory(
    authHeaders?: Record<string, string>
  ): Promise<PlanSwitchHistory[]> {
    const debugId = `SWITCH-HISTORY-${Date.now()}`;
    console.log(`📜 [${debugId}] ========== GET SWITCH HISTORY ==========`);

    try {
      const headers = this.getHeaders(authHeaders);
      
      console.log(`📤 [${debugId}] Fetching switch history...`);
      const startTime = Date.now();
      
      const response = await axios.get<PlanSwitchHistory[]>(
        `${WORKER_API_URL}/subscriptions/switch-history`,
        { headers }
      );
      
      const duration = Date.now() - startTime;
      console.log(`✅ [${debugId}] Switch history fetched successfully in ${duration}ms:`, {
        historyCount: response.data.length
      });
      
      return response.data;
    } catch (error) {
      console.error(`💥 [${debugId}] Get switch history failed:`, error);
      throw error;
    }
  }
}

export const subscriptionService = new SubscriptionService();
